import { acpAgentCatalogEntryForAlias } from '@crowdex/agent-contracts';
import { crowdexKnownAgentFamilyForAgentId } from '@crowdex/agent-runtime/identity';
import { isRegistryAgentOption } from '@/lib/agent-runtime/acp-agent-catalog';
import type { NativeAgent } from '@/lib/native-desktop';

export type AgentRuntime = 'cloud' | 'local';

/** Basename of an agent command, so `/usr/bin/codex-acp` matches `codex-acp`. */
export function agentCommandName(command: string): string {
  const normalized = command.replace(/\\/g, '/');
  const slash = normalized.lastIndexOf('/');
  const name = slash === -1 ? normalized : normalized.slice(slash + 1);
  return name.endsWith('.exe') ? name.slice(0, -'.exe'.length) : name;
}

/** True when two spawn commands refer to the same agent binary. */
export function commandsMatch(left: string, right: string): boolean {
  return agentCommandName(left) === agentCommandName(right);
}

function uniqueByCommand(agents: readonly NativeAgent[]): NativeAgent[] {
  const unique: NativeAgent[] = [];
  const seen = new Set<string>();
  for (const agent of agents) {
    const key = agentCommandName(agent.command);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(agent);
  }
  return unique;
}

/**
 * Agents offered for a runtime. Local discovered binaries keep their ids when
 * they can also run in the cloud; catalog entries fill in products that are
 * not installed locally.
 */
export function agentsForRuntime(
  discovered: readonly NativeAgent[],
  runtime: AgentRuntime,
): NativeAgent[] {
  return uniqueByCommand(
    discovered.filter((agent) =>
      runtime === 'cloud' ? isRegistryAgentOption(agent) : !isRegistryAgentOption(agent),
    ),
  );
}

/** Same product (Codex, Claude, …) on the requested runtime. */
export function resolveAgentForRuntime(
  discovered: readonly NativeAgent[],
  command: string | undefined,
  runtime: AgentRuntime,
): NativeAgent | undefined {
  const pool = agentsForRuntime(discovered, runtime);
  if (command !== undefined && command.length > 0) {
    const match = pool.find((candidate) => commandsMatch(candidate.command, command));
    if (match !== undefined) return match;
  }
  return pool[0];
}

/** Resolve a stored agent id to a live agent. */
export function resolvePersistedAgent(
  discovered: readonly NativeAgent[],
  storedId: string | null | undefined,
): NativeAgent | undefined {
  if (storedId === null || storedId === undefined || storedId.length === 0) return undefined;
  const direct =
    discovered.find((agent) => agent.id === storedId) ??
    uniqueByCommand(discovered).find((agent) => commandsMatch(agent.command, storedId));
  if (direct !== undefined) return direct;
  const family = crowdexKnownAgentFamilyForAgentId(storedId);
  if (family === null) return undefined;
  return discovered.find(
    (agent) =>
      crowdexKnownAgentFamilyForAgentId(agent.id) === family ||
      crowdexKnownAgentFamilyForAgentId(agent.command) === family,
  );
}

/** Map a composer model choice to its cloud agent command, or null for "auto". */
export function preferredAgentCommand(model: string): string | null {
  if (model === 'auto') return null;
  return acpAgentCatalogEntryForAlias(model)?.id ?? model;
}
